import { AlertAction, AlertType } from "../../ShellCommon/Controls/alert/alert";
import {
  TelemetryService,
  TelemetryServiceImplementation,
  CustomKeyValues
} from "../../ShellCommon/logger/telemetry-service";
import { InformativeAlertService } from "../sdDirectives/informative-alert-service";
import { ServiceDeskConfig } from "../Startup/serviceDeskConfig";
import { GlobalTelemetryConstants } from "../../ShellCommon/logger/telemetry-constants";
import { SdServicesSharedEnums } from "./sdServices-shared-enums";
import * as Jsll from "../../ShellCommon/logger/jsll-types";

/**
 * An error details object.
 */
export interface ErrorDetails extends Error {
  /**
   * any code provided from error
   */
  code?: string;
  /**
   * complete stack trace from where error occured, generally comes automatically from your browser's Error object.
   */
  stack?: any;
  /**
   * additional error context, will be stringified
   */
  data?: {} | string;
  /**
   * indicates whether the error was handled in code or not
   */
  unhandled?: boolean;
  /**
   * a number from 0 - 3 to indicate how bad the error/exception was that occured
   */
  severity?: number;
  /**
   * description of type of error that took place (ie playback, general)
   */
  type?: string;
  /**
   * location at which error/exception took place in code
   */
  lineNumber?: number;
  /**
   * whether or not the error was displayd as a notification to end-user
   */
  displayed?: boolean;
  /**
   * function/method where error/exception happened
   */
  method?: string;
}

/**
 * Data structure for describing messages that should be shown to the user.
 */
export interface UserVisibleMessage {
  /**
   * The text to display as the content of the user visible message.
   */
  displayText: string;
  /**
   * The title for the user visible message. If it is not specified, a default title for the severity level will be used.
   */
  title?: string;
  /**
   * A unique Id to identify a particular visible message or a group of visible messages being passed.
   */
  uId?: string;
  /**
   * whether or not the we hide the close button for the alert
   */
  hideCloseButton?: boolean;
  /**
   * Alert actions that will be visible in the notification that the user can click on.
   */
  alertActions?: AlertAction[];
}

/**
 * Contract for logging additional telemetry for scenarios.
 */
export interface ExtendedTelemetryInfo {
  /**
   * Service name or app name override
   */
  serviceName?: string;
  /**
   * Name of the scenario
   */
  scenario?: string;
  /**
   * Name of the scenario step
   */
  scenarioStep?: string;
  /**
   * Any scenario error code which can categorize the error type
   */
  scenarioError?: string;
  /**
   * The page action behavior involved in the event.
   */
  behavior?: any;
  /**
   * Additional info to be logged as pageTags
   */
  pageTags?: CustomKeyValues;
  /**
   * Page type to be logged in the event.
   */
  pageType?: string;
}

/**
 * The logging service for Service Desk.
 */
export interface LoggingService {
  /**
   * This method tracks errors, information, and warnings via Telemetry (when necessary) and will log to the develop console if the consoleVerbosity setting is <= the submitted sdServicesSharedEnums.logType.
   *
   * @param logType The verbosity of the log message. Use the SdServicesSharedEnums.logType enumeration.
   * @param details The message or data object (which will be stringified) to log.
   * @param title Optional, the title to use for the user-visible notification. If not specified while displayText is specified, the Title will be defaulted according to the sdServicesSharedEnums.logType.
   * @param displayText Optional, the description to use for the user-visible notification.
   * @param telemetryInfo Optional, additional telemetry info to be logged like scenario, scenarioStep, pageTags etc
   */
  log(
    logType: any,
    details: {} | string,
    title?: string,
    displayText?: string,
    telemetryInfo?: ExtendedTelemetryInfo,
    logImmediately?: boolean
  ): void;

  /**
   * This method tracks errors, information, and warnings via Telemetry (when necessary) and will log to the develop console if the consoleVerbosity setting is <= the submitted sdServicesSharedEnums.logType.
   * It accepts the UserVisibleMessage to show alerts in the UI.
   *
   * @param logType The verbosity of the log message. Use the SdServicesSharedEnums.logType enumeration.
   * @param details The message or data object (which will be stringified) to log.
   * @param userVisibleMessage The user visible message to be raised when logging this event. If a string is specified, a default title is used and the string becomes the display text.
   * @param telemetryInfo Optional, additional telemetry info to be logged like scenario, scenarioStep, pageTags etc
   * */
  logEvent(
    logType: any,
    details: {} | string,
    userVisibleMessage: UserVisibleMessage,
    telemetryInfo?: ExtendedTelemetryInfo,
    errorCode?: string,
    logImmediately?: boolean
  ): void;

  /**
   * This method tracks errors via Telemetry and to the develop console if the consoleVerbosity setting allows sdServicesSharedEnums.logType.Error
   *
   * @param error The message, data object (which will be stringified), or JS Error to log. If you want to use an ErrorDetails object that isn't a JS error, prefer the logException method.
   * @param code The error code, the identifier for the type of error being thrown. If a number is passed, it will be coalesced into string.
   * @param userVisibleMessage The user visible message to be raised when logging this event. If a string is specified, a default title is used and the string becomes the display text.
   * @param telemetryInfo Optional, additional telemetry info to be logged like scenario, scenarioStep, pageTags etc
   */
  logError(
    error: {} | string | ErrorDetails,
    code?: string | number,
    userVisibleMessage?: UserVisibleMessage | string,
    telemetryInfo?: ExtendedTelemetryInfo,
    logImmediately?: boolean
  ): void;

  /**
   * A public function for pushing exceptions to Asimov (via telemetryService) and logging the same to the developer console. Prefer the logError function, which supports submitted ErrorDetails.
   *
   * @param error The javascript exception or similarly shaped data object you wish to log.
   * @param userVisibleMessage The user visible message to be raised when logging this event. If a string is specified, a default title is used and the string becomes the display text.
   * @param telemetryInfo Optional, additional telemetry info to be logged like scenario, scenarioStep, pageTags etc
   */
  logException(
    error: ErrorDetails,
    userVisibleMessage?: UserVisibleMessage | string,
    telemetryInfo?: ExtendedTelemetryInfo,
    logImmediately?: boolean
  ): void;

  /**
   * This method tracks information messages via Telemetry and to the develop console if the consoleVerbosity setting allows sdServicesSharedEnums.logType.Information
   *
   * @param info The message or data object (which will be stringified) to log.
   * @param userVisibleMessage The user visible message to be raised when logging this event. If a string is specified, a default title is used and the string becomes the display text.
   * @param telemetryInfo Optional, additional telemetry info to be logged like scenario, scenarioStep, pageTags etc
   */
  logInfo(
    info: {} | string,
    userVisibleMessage?: UserVisibleMessage | string,
    telemetryInfo?: ExtendedTelemetryInfo,
    logImmediately?: boolean
  ): void;

  /**
   * This method tracks warning messages via Telemetry and to the develop console if the consoleVerbosity setting allows sdServicesSharedEnums.logType.Warning
   *
   * @param warning The message or data object (which will be stringified) to log.
   * @param userVisibleMessage The user visible message to be raised when logging this event. If a string is specified, a default title is used and the string becomes the display text.
   * @param telemetryInfo Optional, additional telemetry info to be logged like scenario, scenarioStep, pageTags etc.
   */
  logWarning(
    warning: {} | string,
    userVisibleMessage?: UserVisibleMessage | string,
    telemetryInfo?: ExtendedTelemetryInfo,
    logImmediately?: boolean
  ): void;

  /**
   * Closes a user visible message that is currently open on the screen.
   */
  closeUserVisibleMessage(uId: string): void;
}

/**
 * This type is deprecated, please instead use the "LoggingService" type. This type will eventually be removed.
 */
export interface LogViewService extends LoggingService {}

const correlationVectorLocalStorageKey = "SDLoggingServiceCorrelationVector";

export class LoggingServiceImplementation implements LoggingService {
  private registeredScope: ng.IScope;
  // @ngInject
  constructor(
    private telemetryService: TelemetryService,
    private informativeAlertService: InformativeAlertService,
    private sdConfig: ServiceDeskConfig,
    private telemetryConstants: GlobalTelemetryConstants,
    private sdServicesSharedEnums: SdServicesSharedEnums
  ) {}

  public log(
    logType: any,
    details: {} | string,
    title?: string,
    displayText?: string,
    telemetryInfo?: ExtendedTelemetryInfo,
    logImmediately?: boolean
  ): void {
    if (details) {
      let userVisibleMessage = displayText ? { displayText: displayText, title: title } : undefined;
      switch (logType) {
        case this.sdServicesSharedEnums.logType.Error:
          this.logError(details, undefined, userVisibleMessage, telemetryInfo, logImmediately);
          break;
        case this.sdServicesSharedEnums.logType.Info:
          this.logInfo(details, userVisibleMessage, telemetryInfo, logImmediately);
          break;
        case this.sdServicesSharedEnums.logType.Warning:
          this.logWarning(details, userVisibleMessage, telemetryInfo, logImmediately);
          break;
      }
    }
  }

  public logEvent(
    logType: any,
    details: {} | string,
    userVisibleMessage: UserVisibleMessage,
    telemetryInfo?: ExtendedTelemetryInfo,
    errorCode?: string,
    logImmediately?: boolean
  ): void {
    if (details) {
      switch (logType) {
        case this.sdServicesSharedEnums.logType.Error:
          this.logError(details, errorCode, userVisibleMessage, telemetryInfo, logImmediately);
          break;
        case this.sdServicesSharedEnums.logType.Info:
          this.logInfo(details, userVisibleMessage, telemetryInfo, logImmediately);
          break;
        case this.sdServicesSharedEnums.logType.Warning:
          this.logWarning(details, userVisibleMessage, telemetryInfo, logImmediately);
          break;
      }
    }
  }

  public logError(
    error: {} | string | ErrorDetails,
    code?: string | number,
    userVisibleMessage?: UserVisibleMessage | string,
    telemetryInfo?: ExtendedTelemetryInfo,
    logImmediately?: boolean
  ) {
    telemetryInfo = telemetryInfo || {};
    if (error instanceof Error) {
      // Forward the log invocation on to the LogException event.
      if (code) {
        (<any>error).code = code.toString();
      }
      this.logException(<ErrorDetails>error, userVisibleMessage, telemetryInfo);
    } else {
      let pageTags = this.getPageTags(telemetryInfo, code ? code.toString() : "");
      if (userVisibleMessage) {
        this.processUserVisibleMessage(this.sdServicesSharedEnums.logType.Error, userVisibleMessage);
        if (telemetryInfo.scenario) {
          // trigger a PageAction event when a user message is shown.
          this.telemetryService.pushPageActionEvent(
            this.telemetryConstants.pageActionType.auto,
            telemetryInfo.behavior,
            undefined,
            this.getContent(error),
            pageTags
          );
        } else {
          this.telemetryService.pushErrorEvent(
            error,
            code ? code.toString() : "",
            "",
            "",
            false,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            telemetryInfo.serviceName,
            logImmediately
          );
        }
      } else {
        this.telemetryService.pushErrorEvent(
          error,
          code ? code.toString() : "",
          "",
          "",
          false,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          telemetryInfo.serviceName,
          logImmediately
        );
      }
      if (this.sdConfig.loggingVerbosityLevel >= this.sdConfig.LoggingVerbosityLevel.Error) {
        console.error(error);
      }
    }
  }

  public logException(
    error: ErrorDetails,
    userVisibleMessage?: UserVisibleMessage | string,
    telemetryInfo?: ExtendedTelemetryInfo,
    logImmediately?: boolean
  ): void {
    telemetryInfo = telemetryInfo || {};
    let pageTags = this.getPageTags(telemetryInfo, error.code);
    if (userVisibleMessage) {
      this.processUserVisibleMessage(this.sdServicesSharedEnums.logType.Error, userVisibleMessage);
      if (telemetryInfo.scenario) {
        // trigger a PageAction event when a user message is shown.
        this.telemetryService.pushPageActionEvent(
          this.telemetryConstants.pageActionType.auto,
          telemetryInfo.behavior,
          undefined,
          this.getContent(error),
          pageTags
        );
      } else {
        this.telemetryService.pushErrorEvent(
          error.message,
          error.code,
          error.stack,
          error.data,
          !!error.unhandled,
          error.name,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          telemetryInfo.serviceName,
          logImmediately
        );
      }
    } else {
      this.telemetryService.pushErrorEvent(
        error.message,
        error.code,
        error.stack,
        error.data,
        !!error.unhandled,
        error.name,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        telemetryInfo.serviceName,
        logImmediately
      );
    }
    if (this.sdConfig.loggingVerbosityLevel >= this.sdConfig.LoggingVerbosityLevel.Error) {
      console.error(error);
    }
  }

  public logInfo(
    info: {} | string,
    userVisibleMessage?: UserVisibleMessage | string,
    telemetryInfo?: ExtendedTelemetryInfo,
    logImmediately?: boolean
  ): void {
    telemetryInfo = telemetryInfo || {};
    let pageTags = this.getPageTags(telemetryInfo);
    if (userVisibleMessage) {
      this.processUserVisibleMessage(this.sdServicesSharedEnums.logType.Info, userVisibleMessage);
      if (telemetryInfo.scenario) {
        // trigger a PageAction event when a user message is shown.
        this.telemetryService.pushPageActionEvent(
          this.telemetryConstants.pageActionType.auto,
          telemetryInfo.behavior,
          undefined,
          this.getContent(info),
          pageTags
        );
      } else {
        this.telemetryService.pushInformationEvent(
          this.telemetryConstants.informationTypes.Information,
          angular.isString(info) ? info : JSON.stringify(info),
          telemetryInfo.serviceName,
          logImmediately
        );
      }
    } else {
      // TODO: After custom events are well-supported for events currently using "Information", this needs to be hidden behind a runtime switch for diagnostic purposes.
      this.telemetryService.pushInformationEvent(
        this.telemetryConstants.informationTypes.Information,
        angular.isString(info) ? info : JSON.stringify(info),
        telemetryInfo.serviceName,
        logImmediately
      );
    }

    if (this.sdConfig.loggingVerbosityLevel >= this.sdConfig.LoggingVerbosityLevel.Information) {
      console.info(info);
    }
  }

  public logWarning(
    warning: {} | string,
    userVisibleMessage?: UserVisibleMessage | string,
    telemetryInfo?: ExtendedTelemetryInfo,
    logImmediately?: boolean
  ): void {
    telemetryInfo = telemetryInfo || {};
    let pageTags = this.getPageTags(telemetryInfo);
    if (userVisibleMessage) {
      this.processUserVisibleMessage(this.sdServicesSharedEnums.logType.Warning, userVisibleMessage);
      if (telemetryInfo.scenario) {
        // trigger a PageAction event when a user message is shown.
        this.telemetryService.pushPageActionEvent(
          this.telemetryConstants.pageActionType.auto,
          telemetryInfo.behavior,
          undefined,
          this.getContent(warning),
          pageTags
        );
      } else {
        this.telemetryService.pushInformationEvent(
          this.telemetryConstants.informationTypes.Warning,
          angular.isString(warning) ? warning : JSON.stringify(warning),
          telemetryInfo.serviceName,
          logImmediately
        );
      }
    } else {
      this.telemetryService.pushInformationEvent(
        this.telemetryConstants.informationTypes.Warning,
        angular.isString(warning) ? warning : JSON.stringify(warning),
        telemetryInfo.serviceName,
        logImmediately
      );
    }

    this.telemetryService.pushInformationEvent(
      this.telemetryConstants.informationTypes.Warning,
      angular.isString(warning) ? warning : JSON.stringify(warning)
    );
    if (this.sdConfig.loggingVerbosityLevel >= this.sdConfig.LoggingVerbosityLevel.Warning) {
      console.warn(warning);
    }
  }

  public registerErrorCtrl($scope: ng.IScope): void {
    if (!this.registeredScope) {
      this.registeredScope = $scope;
    }
  }

  public closeUserVisibleMessage(uId: string): void {
    this.informativeAlertService.closeLogByUId(uId);
  }

  private processUserVisibleMessage(logType: any, userVisibleMessage: UserVisibleMessage | string): void {
    let coalescedMessage: UserVisibleMessage = !angular.isString(userVisibleMessage)
      ? <UserVisibleMessage>userVisibleMessage
      : {
          uId: "loggingSvcStringOnlyUId",
          displayText: <string>userVisibleMessage
        };

    if (coalescedMessage && (coalescedMessage.title || coalescedMessage.displayText)) {
      this.informativeAlertService.pushLog({
        alertType: this.convertLogTypeToAlertType(logType),
        title: coalescedMessage.title,
        displayText: coalescedMessage.displayText,
        uId: coalescedMessage.uId,
        alertActions: coalescedMessage.alertActions,
        hideCloseButton: coalescedMessage.hideCloseButton
      });
    }
  }

  private convertLogTypeToAlertType(logType: any): AlertType {
    // Before you think you're fixing a bug when you see the weird code below, keep in mind that MWF's alert currently shows
    //  Errors in a "Warning" style and Warnings in an "Error" style. They do not think this is a bug.
    switch (logType) {
      case this.sdServicesSharedEnums.logType.Error:
        return "Warning";
      case this.sdServicesSharedEnums.logType.Warning:
        return "Error";
      case this.sdServicesSharedEnums.logType.Info:
      default:
        return "Information";
    }
  }

  private getPageTags(telemetryInfo?: ExtendedTelemetryInfo, errorCode?: string) {
    let pageTags = <Jsll.PageTags>{};
    if (telemetryInfo.scenario) {
      let metaTags = <CustomKeyValues>{};
      if (telemetryInfo.pageType) {
        metaTags[this.telemetryConstants.pageMetadataTags.type] = telemetryInfo.pageType;
      }
      pageTags[this.telemetryConstants.pageMetadataTags.scenario] = telemetryInfo.scenario;
      pageTags[this.telemetryConstants.pageMetadataTags.scenarioStep] = telemetryInfo.scenarioStep;
      if (telemetryInfo.scenarioError || errorCode) {
        pageTags[this.telemetryConstants.pageMetadataTags.scenarioError] = telemetryInfo.scenarioError || errorCode;
      }
      if (telemetryInfo.serviceName) {
        pageTags.serviceName = telemetryInfo.serviceName;
      }
      pageTags = angular.merge({}, pageTags, telemetryInfo.pageTags);
    }
    return pageTags;
  }

  private getContent(data: {} | string | ErrorDetails) {
    if (typeof data === "string") {
      return { error: data };
    } else {
      return data;
    }
  }
}

export class LoggingSvcRun {
  // @ngInject
  constructor(
    $window: ng.IWindowService,
    loggingService: LoggingServiceImplementation,
    telemetryService: TelemetryServiceImplementation
  ) {
    if ($window != $window.top) {
      return;
    }

    let currentCorrelationVector = telemetryService.getCorrelationVector().split(".")[0];
    if ($window.localStorage.getItem(correlationVectorLocalStorageKey)) {
      let storedcorrelationVector = $window.localStorage.getItem(correlationVectorLocalStorageKey).split(".")[0];
      if (storedcorrelationVector !== currentCorrelationVector) {
        $window.localStorage.setItem(correlationVectorLocalStorageKey, currentCorrelationVector);
        loggingService.logInfo(
          `Found different correlation vector, moving from ${storedcorrelationVector} -> ${currentCorrelationVector}`
        );
      }
    } else {
      $window.localStorage.setItem(correlationVectorLocalStorageKey, currentCorrelationVector);
    }
  }
}
